import React from 'react'
import PropTypes from 'prop-types'
import styled, { createGlobalStyle } from 'styled-components'
import loadable from '@loadable/component'
import Prism from 'prismjs'

import { SUPPORTED_LANGUAGES } from '@anvilco-pkg/sanity'

import 'prismjs/themes/prism-okaidia.css'

import theme, { rgba } from 'theme'
import { classString } from 'helpers'

// NOTE: babel plugin for prism injects these:
// - '.code-toolbar'
// - .toolbar
//
// we should get rid of them a diff way, but this works for now
const GlobalStyle = createGlobalStyle`
  .code-toolbar {
    width: 100%;
  }

  .toolbar {
    display: none
  }
`

const StyledPre = styled.pre`
  padding: 0px;
  border: none;

  code {
    font-weight: ${theme.fontWeights.normal};
    white-space: pre;
    color: ${theme.colors.white};
    font-family: 'DM Mono', monospace;
  }

  &[class*='language-'] {
    font-size: ${theme.fontSizes[40]}px;
    background: ${rgba(theme.colors.black, 0.9)};
    color: white;

    .token.punctuation {
      color: white;
    }
  }

  & ::selection {
    background: white;
    color: ${theme.colors.black};
  }

  .token.function {
    color: ${theme.colors.yellow};
  }
  .token.property {
    color: ${theme.colors.red};
  }
  .token.string {
    color: ${theme.colors.green};
  }
  .token.comment {
    color: ${theme.colors.blacks[30]};
  }
`

const getLanguage = (languageOrLanguageAliases) => {
  function getStrLanguage (str) {
    return SUPPORTED_LANGUAGES.find((l) => l.key === str.toLowerCase()) || ''
  }

  if (typeof languageOrLanguageAliases === 'string') {
    return getStrLanguage(languageOrLanguageAliases)
  }

  for (const str of languageOrLanguageAliases) {
    const lang = getStrLanguage(str)
    if (lang) return lang
  }

  return ''
}

class Code extends React.Component {
  state = {
    langLoaded: false,
  }

  componentDidMount () {
    const { languageOrLanguageAliases } = this.props
    const { key: language, prismLanguage } = getLanguage(
      languageOrLanguageAliases
    )

    const lang = prismLanguage || language

    const loadLang = () => {
      loadable
        .lib(() => import(`prismjs/components/prism-${lang}`))
        .load()
        .then((languageModule) => {
          this.setState({ langLoaded: true })
        })
    }

    // this is dumb, but needed: https://github.com/PrismJS/prism/issues/1400#issuecomment-485847919
    if (lang === 'php') {
      loadable
        .lib(() => import('prismjs/components/prism-markup-templating'))
        .load()
        .then(() => {
          loadLang()
        })
    } else {
      loadLang()
    }
  }

  render () {
    // The code snippet you want to highlight, as a string
    const { children, languageOrLanguageAliases, className, ...others } =
      this.props

    const { key: language } = getLanguage(languageOrLanguageAliases)

    let html
    try {
      html = Prism.highlight(children, Prism.languages[language], language)
    } catch (e) {
      // eat the error, Prism doesn't _initially_ support the language,
      // but we load it dynamically
    }
    return (
      html && (
        <>
          <StyledPre
            {...others}
            className={classString([
              `language-${language}`,
              'prism-code',
              className,
            ])}
          >
            <code dangerouslySetInnerHTML={{ __html: html }} />
          </StyledPre>
          <GlobalStyle />
        </>
      )
    )
  }
}

Code.propTypes = {
  children: PropTypes.string.isRequired,
  languageOrLanguageAliases: PropTypes.oneOfType([
    PropTypes.oneOf(SUPPORTED_LANGUAGES.map((l) => l.key)),
    PropTypes.arrayOf(PropTypes.oneOf(SUPPORTED_LANGUAGES.map((l) => l.key))),
  ]),
  className: PropTypes.string,
}

Code.defaultProps = {
  languageOrLanguageAliases: 'javascript',
  children: 'function () { \n  return 4;\n}',
}

export default Code
